import { Link, useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { userHasPermission } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useBranding } from "@/hooks/useBranding";
import type { Permission } from "@shared/contracts/permissions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard,
  Users,
  ScrollText,
  Coins,
  Blocks,
  ListChecks,
  Zap,
  Package,
  RefreshCcw,
  Mail,
  FileText,
  FileBarChart2,
  Ticket,
  Inbox,
  Copy,
  ClipboardList,
  BookOpen,
  Bot,
  FolderKanban,
  CalendarRange,
  Gauge,
  Cable,
  Settings,
  Shield,
  MailPlus,
  SearchCode,
  FolderOpen,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import logoDefault from "@/assets/logo-default.png";

interface NavChild {
  name: string;
  href: string;
  search?: string;
  countKey?: string;
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  children?: NavChild[];
}

interface NavSection {
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
  permission?: Permission;
}

const sections: NavSection[] = [
  {
    label: "",
    items: [
      { name: "Dashboard", href: "/", icon: LayoutDashboard },
    ],
  },
  {
    label: "COMMERCIAL",
    permission: "clients.read",
    items: [
      { name: "Clients", href: "/clients", icon: Users },
      { name: "Contracts", href: "/contracts", icon: ScrollText },
      { name: "Budgets", href: "/budgets", icon: Coins },
      { name: "Service Types", href: "/service-types", icon: Blocks },
      { name: "Service Items", href: "/service-items", icon: ListChecks },
    ],
  },
  {
    label: "BILLING",
    permission: "services.read",
    items: [
      { name: "Services", href: "/services", icon: Zap },
      { name: "Products", href: "/products", icon: Package },
      { name: "Renewals", href: "/renewals", icon: RefreshCcw },
      { name: "Email Logs", href: "/email-logs", icon: Mail },
      { name: "Invoices", href: "/invoices", icon: FileText },
      { name: "Reporting", href: "/reporting", icon: FileBarChart2 },
    ],
  },
  {
    label: "SUPPORT",
    permission: "tickets.read",
    items: [
      {
        name: "Tickets",
        href: "/tickets",
        icon: Ticket,
        children: [
          { name: "Open Tickets", href: "/tickets" },
          { name: "New", href: "/tickets", search: "?status=new", countKey: "new" },
          { name: "In Progress", href: "/tickets", search: "?status=in_progress", countKey: "in_progress" },
          { name: "Pending", href: "/tickets", search: "?status=pending", countKey: "pending" },
          { name: "Closed", href: "/tickets", search: "?status=closed", countKey: "closed" },
          { name: "Spam", href: "/tickets", search: "?status=spam", countKey: "spam" },
          { name: "Deleted", href: "/tickets", search: "?status=deleted", countKey: "deleted" },
        ],
      },
      { name: "Channels", href: "/channels", icon: Inbox },
      { name: "Canned Replies", href: "/canned-replies", icon: Copy },
    ],
  },
  {
    label: "INTAKE",
    permission: "work_requests.read",
    items: [
      { name: "Work Requests", href: "/work-requests", icon: ClipboardList },
      { name: "Knowledgebase", href: "/knowledgebase", icon: BookOpen },
      { name: "AI Scoping Logs", href: "/ai-scoping-logs", icon: Bot },
    ],
  },
  {
    label: "DELIVERY",
    permission: "projects.read",
    items: [
      { name: "Projects", href: "/projects", icon: FolderKanban },
      { name: "Bookings", href: "/bookings", icon: CalendarRange },
      { name: "Capacity", href: "/capacity", icon: Gauge },
    ],
  },
  {
    label: "CLIENT FILES",
    permission: "files.read",
    items: [{ name: "Files", href: "/files", icon: FolderOpen }],
  },
  {
    label: "PROSPECTING",
    permission: "site_audits.read",
    items: [{ name: "Site Audits", href: "/site-audits", icon: SearchCode }],
  },
  {
    label: "INTEGRATIONS",
    permission: "integrations.read",
    items: [{ name: "Integrations", href: "/integrations", icon: Cable }],
  },
  {
    label: "ADMINISTRATION",
    adminOnly: true,
    items: [
      { name: "Settings", href: "/settings", icon: Settings },
      { name: "Email Templates", href: "/email-templates", icon: MailPlus },
      { name: "Users", href: "/users", icon: Shield },
      { name: "Audit Log", href: "/audit-log", icon: ScrollText },
    ],
  },
];

export function AdminSidebar() {
  const [location, navigate] = useLocation();
  const currentSearch = useSearch();
  const { user } = useAuth();
  const { logoUrl, brandName } = useBranding();

  const permissions = user?.effectivePermissions ?? [];
  const isAdmin =
    userHasPermission(permissions, "users.read") ||
    userHasPermission(permissions, "settings.read") ||
    userHasPermission(permissions, "audit_logs.read") ||
    userHasPermission(permissions, "email_templates.read");
  const initials =
    (user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? user?.email?.[0] ?? "");

  const { data: ticketCounts } = useQuery<Record<string, number>>({
    queryKey: ["/api/support/tickets/counts"],
    queryFn: getQueryFn({ on401: "throw" }),
    refetchInterval: 60_000,
  });

  const totalTickets = ticketCounts
    ? Object.entries(ticketCounts)
      .filter(([status]) => !["closed", "spam", "deleted"].includes(status))
      .reduce((total, [, count]) => total + count, 0)
    : undefined;
  const activeStatus = new URLSearchParams(currentSearch).get("status");

  return (
    <div className="flex h-full w-64 flex-col border-r bg-sidebar">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-sidebar-border">
        <img
          src={logoUrl || logoDefault}
          alt={brandName}
          className="h-8 object-contain"
        />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {sections.map((section) => {
            if (section.adminOnly && !isAdmin) return null;
            if (section.permission && !userHasPermission(permissions, section.permission)) {
              return null;
            }

            return (
              <div key={section.label || "main"}>
                {section.label && (
                  <>
                    <Separator className="my-3" />
                    <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.label}
                    </p>
                  </>
                )}
                {section.items.map((item) => {
                  const isActive =
                    item.href === "/"
                      ? location === "/"
                      : location.startsWith(item.href);

                  const isExpanded = isActive && !!item.children;

                  return (
                    <div key={item.href + item.name}>
                      <Link href={item.href}>
                        <span
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer",
                            isActive
                              ? "bg-sidebar-accent text-sidebar-accent-foreground"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                          )}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {item.name}
                        </span>
                      </Link>

                      {/* Expandable children */}
                      {isExpanded && item.children && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l border-sidebar-border pl-3">
                          {item.children.map((child) => {
                            const childFullPath = child.href + (child.search || "");
                            const childStatus = child.search
                              ? new URLSearchParams(child.search).get("status")
                              : null;
                            const isChildActive = child.search
                              ? location.startsWith(child.href) && activeStatus === childStatus
                              : location.startsWith(child.href) && !activeStatus;

                            const countValue = child.countKey
                              ? ticketCounts?.[child.countKey]
                              : !child.countKey && !child.search
                                ? totalTickets
                                : undefined;

                            return (
                              <a
                                key={childFullPath}
                                href={`/admin${childFullPath}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`${child.href}${child.search || ""}`);
                                }}
                                className={cn(
                                  "flex items-center justify-between rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                                  isChildActive
                                    ? "bg-sidebar-accent/70 text-sidebar-accent-foreground"
                                    : "text-sidebar-foreground/60 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground",
                                )}
                              >
                                <span>{child.name}</span>
                                {countValue !== undefined && (
                                  <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
                                    {countValue}
                                  </span>
                                )}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {initials.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {user?.firstName ?? user?.email}
            </p>
            <p className="text-xs text-muted-foreground capitalize">
              {user?.userType ?? "internal"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
